/**
 * EvidencePreviewCard - Preview of Evidence answer in hover card.
 *
 * Shows:
 * - Streaming/loading state
 * - Truncated answer preview with shimmer effect
 * - Citation badges
 * - "Read more in Evidence" link
 */

import { useChipAnswerPreview } from '@/hooks/useChipAnswerPreview';
import { cn } from '@/lib/cn';
import { Loader2 } from 'lucide-react';
import { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { evidencePreviewCardMessages } from './evidence-preview-card.messages';

// ============================================================================
// Types
// ============================================================================

export interface ChipQueryResult {
  chipId: string;
  chatId: string | null;
  answer: string;
  sources: Array<{ title: string; domain: string }>;
  isComplete: boolean;
  error: string | null;
}

export interface EvidencePreviewCardProps {
  /** The query text to show if no answer */
  query: string;
  /** Chip ID for fetching preview */
  chipId: string;
  /** Session ID for chat lookup */
  sessionId: string | null;
  /** Optional streaming query result */
  queryResult?: ChipQueryResult;
  /** Click handler to open Evidence panel */
  onClick?: () => void;
  /** Whether the chip is a red flag (changes styling) */
  isRedFlag?: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

const MAX_PREVIEW_LENGTH = 200;

/**
 * Extract preview content from either streaming result or chat preview.
 * Separated to reduce cognitive complexity in the main component.
 */
function getPreviewContent(
  hasStreamingAnswer: boolean,
  queryResult: ChipQueryResult | undefined,
  chatPreview: { previewText: string | null; isTruncated: boolean }
): { previewText: string | null; isTruncated: boolean } {
  if (hasStreamingAnswer && queryResult) {
    const answer = queryResult.answer;
    const isTruncated = answer.length > MAX_PREVIEW_LENGTH;
    const previewText = isTruncated ? `${answer.slice(0, MAX_PREVIEW_LENGTH)}…` : answer;
    return { previewText, isTruncated };
  }

  return {
    previewText: chatPreview.previewText,
    isTruncated: chatPreview.isTruncated,
  };
}

// ============================================================================
// Main Component
// ============================================================================

export const EvidencePreviewCard = memo(
  ({
    query,
    chipId,
    sessionId,
    queryResult,
    onClick,
    isRedFlag = false,
  }: EvidencePreviewCardProps) => {
    const intl = useIntl();

    // Get answer preview from chat state (fallback when not streaming)
    const chatPreview = useChipAnswerPreview({
      sessionId,
      chipId,
    });

    // Determine streaming state
    const isStreaming = queryResult && !queryResult.isComplete;
    const hasStreamingAnswer = Boolean(queryResult && queryResult.answer.length > 0);

    // Determine what to show
    const isLoading = (isStreaming && !hasStreamingAnswer) || chatPreview.isLoading;

    // Calculate preview text based on source (streaming vs chat)
    const { previewText, isTruncated } = getPreviewContent(
      hasStreamingAnswer,
      queryResult,
      chatPreview
    );

    const citations = chatPreview.citations;

    // Fallback to query if no answer yet
    const truncatedQuery = query.length > 80 ? `${query.slice(0, 80)}…` : query;
    const displayText =
      previewText ??
      intl.formatMessage(evidencePreviewCardMessages.searchingForQuery, {
        query: truncatedQuery,
      });

    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'w-full space-y-3 text-left',
          'rounded-md p-1 transition-colors',
          'hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-outline-focus',
          // Dark background for red flag
          isRedFlag && 'rounded-lg bg-bark-800 p-3'
        )}
      >
        {isLoading ? (
          <div className="flex items-center gap-2">
            <Loader2 size={14} className="animate-spin text-text-tertiary" />
            <p
              className={cn(
                'text-sm',
                isRedFlag ? 'text-sand-200' : 'text-text-tertiary'
              )}
            >
              <FormattedMessage {...evidencePreviewCardMessages.generatingAnswer} />
            </p>
          </div>
        ) : (
          <p
            className={cn(
              'text-sm leading-relaxed',
              isRedFlag ? 'text-sand-50' : 'text-text-primary'
            )}
          >
            {displayText}
          </p>
        )}

        {/* Citations */}
        {citations.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {citations.map((citation) => (
              <span
                key={citation.number}
                className={cn(
                  'rounded px-1.5 py-0.5 text-xs',
                  isRedFlag
                    ? 'bg-bark-700 text-sand-200'
                    : 'bg-surface text-text-secondary'
                )}
              >
                <FormattedMessage
                  {...evidencePreviewCardMessages.citationBadge}
                  values={{ number: citation.number }}
                />
              </span>
            ))}
          </div>
        )}

        {/* Read more link */}
        <div className="flex items-center gap-3 pt-2">
          <div className={cn('h-px flex-1', isRedFlag ? 'bg-bark-600' : 'bg-border')} />
          <p
            className={cn(
              'shrink-0 text-xs',
              isRedFlag ? 'text-sand-300' : 'text-text-tertiary'
            )}
          >
            {isTruncated ? (
              <FormattedMessage {...evidencePreviewCardMessages.readMoreInEvidence} />
            ) : (
              <FormattedMessage {...evidencePreviewCardMessages.openInEvidence} />
            )}
          </p>
          <div className={cn('h-px flex-1', isRedFlag ? 'bg-bark-600' : 'bg-border')} />
        </div>
      </button>
    );
  }
);

EvidencePreviewCard.displayName = 'EvidencePreviewCard';

export default EvidencePreviewCard;
